import { makeStyles } from '@material-ui/styles'
import { listTenants } from 'account/components/userManagement/tenants/new-actions'
import { tenantsSelector } from 'account/components/userManagement/tenants/selectors'
import { listTablePrefs, TablePrefsParams } from 'app/constants'
import ChangeOwnerDialog from 'app/plugins/openstack/components/vms/ChangeOwnerDialog'
import { useVirtualMachinesPagination } from 'app/plugins/openstack/components/vms/useVirtualMachinesPagination'
import store, { RootState } from 'app/store'
import { ArrayElement } from 'core/actions/Action'
import InferActionParams from 'core/actions/InferActionParams'
import PollingData from 'core/components/PollingData'
import SimpleLink from 'core/components/SimpleLink'
import ListContainer from 'core/containers/ListContainer'
import { createGridLinkCell } from 'core/elements/grid/cells/GridLinkCell'
import { GridViewColumn } from 'core/elements/grid/Grid'
import getGridDialogButton from 'core/elements/grid/helpers/getGridDialogButton'
import { GridRowMenuItemSpec } from 'core/elements/grid/hooks/useGridRowMenu'
import {
  GridBatchActionSpec,
  GridDropdownBatchActionGroup,
} from 'core/elements/grid/hooks/useGridSelectableRows'
import { SortingState } from 'core/elements/grid/hooks/useGridSorting'
import Text from 'core/elements/Text'
import ToggleSwitch from 'core/elements/ToggleSwitch'
import Tooltip from 'core/elements/tooltip'
import useGlobalParams from 'core/hooks/useGlobalParams'
import useListAction from 'core/hooks/useListAction'
import useUpdateAction from 'core/hooks/useUpdateAction'
import { createUsePrefParamsHook } from 'core/hooks/useParams'
import { sessionActions, SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import useScopedPreferences from 'core/session/useScopedPreferences'
import Theme from 'core/themes/model'
import { routeManager } from 'core/utils/routes'
import { createResourceLabelsCell } from 'k8s/components/common/entity/labels-and-annotations/helpers'
import DataKeys from 'k8s/DataKeys'
import { listResmgrHosts } from 'openstack/components/infrastructure/actions'
import { IHostDetailsPageTabs } from 'openstack/components/infrastructure/hosts/host-details/model'
import { resmgrHostsSelector } from 'openstack/components/infrastructure/selectors'
import { IVolumeDetailsPageTabs } from 'openstack/components/storage/volumes/volume-details/model'
import {
  deleteVirtualMachine,
  getConsoleEndpoint,
  getNovaVersion,
  listVirtualMachines,
  refreshVmDependencies,
  anyVmDependencyNotLoaded,
} from 'openstack/components/vms/actions'
import { listMigrations, getMigrationsSince } from 'openstack/components/vms/migrations/actions'
import AddFixedIpModal from 'openstack/components/vms/AddFixedIpModal'
import AddFloatingIpDialog from 'openstack/components/vms/AddFloatingIpDialog'
import CloneVirtualMachineModal from 'openstack/components/vms/CloneVirtualMachineModal'
import ConfirmResizeDialog from 'openstack/components/vms/ConfirmResizeDialog'
import DeleteVirtualMachineDialog from 'openstack/components/vms/DeleteVirtualMachineDialog'
import EditVmMetadataModal from 'openstack/components/vms/EditVmMetadataModal'
import EditVmSecurityGroupsModal from 'openstack/components/vms/EditVmSecurityGroupsModal'
import HotplugVmDialog from 'openstack/components/vms/HotplugVmDialog'
import CustomVmLeaseModal from 'openstack/components/vms/leases/CustomVmLeaseModal'
import ManageVolumesModal from 'openstack/components/vms/ManageVolumesModal'
import MigrateVirtualMachineDialog from 'openstack/components/vms/MigrateVirtualMachineDialog'
import PowerVirtualMachineDialog from 'openstack/components/vms/PowerVirtualMachineDialog'
import RebuildVirtualMachineDialog from 'openstack/components/vms/RebuildVirtualMachineDialog'
import RemoveFixedIpDialog from 'openstack/components/vms/RemoveFixedIpDialog'
import RemoveFloatingIpDialog from 'openstack/components/vms/RemoveFloatingIpDialog'
import RenameVirtualMachineDialog from 'openstack/components/vms/RenameVirtualMachineDialog'
import RescueVmDialog from 'openstack/components/vms/RescueVmDialog'
import ResizeVirtualMachineDialog from 'openstack/components/vms/ResizeVirtualMachineDialog'
import { virtualMachinesSelector } from 'openstack/components/vms/selectors'
import SnapshotVirtualMachineDialog from 'openstack/components/vms/SnapshotVirtualMachineDialog'
import UnrescueVmDialog from 'openstack/components/vms/UnrescueVmDialog'
import UpdateMigrationPriorityDialog from 'openstack/components/vms/UpdateMigrationPriorityDialog'
import { IVmDetailsPageTabs } from 'openstack/components/vms/vm-details/model'
import VmErrorModal from 'openstack/components/vms/VmErrorModal'
import VMServerSidePagination from 'openstack/components/vms/VMServerSidePagination'
import {
  defaultVMFilterParams,
  VMServerSideSearch,
} from 'openstack/components/vms/VMServerSideSearch'
import VMServerSideSort from 'openstack/components/vms/VMServerSideSort'
import VmStateCellComponent from 'openstack/components/vms/VmStateCellComponent'
import { getHideCondForDeleteOnTermination, humanReadableSize } from 'openstack/helpers'
import { insertAll, keys, pick, prop } from 'ramda'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { formatTableDate } from 'utils/misc'
import AddServerGroupDialog from './AddServerGroupDialog'
import DeleteOnTerminationDialog from './DeleteOnTerminationDialog'
import GetDisabledTooltipForVMs, { canMigrateVMs } from './GetDisabledTooltipForVMs'
import { options } from './MigrationPriorityPicklist'
import RemoveServerGroupDialog from './RemoveServerGroupDialog'
import {
  ACTION_LIMITATION_TOOLTIPS,
  TOOLTIP_MESSAGES,
  VOLUME_BACKED_VM_ERROR_MSG,
} from './tooltipConstants'
import { isVolumeBackedVm } from './helpers'
import CreateVirtualMachineModal from './CreateVirtualMachineModal'

const { dispatch } = store

export enum IVirtualMachinesListPageTabs {
  VirtualMachinesList = 'list',
  ResourceUtilization = 'resource-utilization',
}

type ModelDataKey = DataKeys.OpenstackVirtualMachines
type SelectorModel = ArrayElement<ReturnType<typeof virtualMachinesSelector>>
type ActionParams = InferActionParams<typeof listVirtualMachines>
// @fixme using a type here because of https://github.com/microsoft/TypeScript/issues/15300
type Params = ActionParams & {}

const requiredParams: Array<keyof ActionParams> = []
const defaultParams: Params & SortingState = {
  orderBy: 'created',
  orderDirection: 'desc',
}

const leaseActionText = {
  delete: 'Delete',
  'power off': 'Stop',
}

const usePrefParams = createUsePrefParamsHook<Params & TablePrefsParams>(
  'OpenstackVirtualMachines',
  listTablePrefs,
)

const searchTargets = [
  'name',
  'id',
  'OS-EXT-SRV-ATTR:hypervisor_hostname',
  'OS-EXT-STS:task_state',
  'OS-EXT-STS:vm_state',
  'flavorName',
  'imageName',
  'ownerName',
  'volumeNames',
  'tenantName',
  'securityGroupNames',
  'serverGroup.name',
  'serverGroup.policy',
  'addresses',
  'state',
  'clusterName',
  'metadata',
]

const searchFunctions = [
  {
    property: 'addresses',
    searchFn: (value, searchString) => {
      const networkNames = keys(value)
      const addresses = networkNames?.reduce((accum, network) => {
        const interfaces = value[network]
        const ips = interfaces.map((iface) => iface.addr)
        return [...accum, ...ips]
      }, [])
      return networkNames.some(
        (name: string) =>
          name.toLocaleLowerCase().includes(searchString.toLocaleLowerCase()) ||
          addresses.some((addr) =>
            addr.toLocaleLowerCase().includes(searchString.toLocaleLowerCase()),
          ),
      )
    },
  },
  {
    property: 'metadata',
    searchFn: (value, searchString) => {
      const metadataKeys = keys(value)
      const metadataValues = metadataKeys?.reduce((accum, key) => {
        return [...accum, value[key]]
      }, [])
      return [...metadataKeys, ...metadataValues].includes(searchString)
    },
  },
]

const IpAddressesCellComponent = ({ value }) => {
  const classes = useStyles()

  const ipAddressKeys = keys(value)
  return (
    <div className={classes.addresses}>
      {ipAddressKeys?.map((key: string) => {
        return (
          <div key={key}>
            <div>
              <b>{key}</b>
            </div>
            {value[key].map((address) => (
              <div key={address.addr}>{address.addr}</div>
            ))}
          </div>
        )
      })}
    </div>
  )
}

const FlavorCellComponent = ({ value, item }) => {
  const classes = useStyles()
  const flavor = item?.flavor
  const hotplugMemory = Number(item?.metadata?.['HOTPLUG_MEMORY'])
  const hotplugVcpus = Number(item?.metadata?.['HOTPLUG_CPU'])
  return (
    <div className={classes.inlineBlock}>
      <Tooltip
        customBody={
          <div className={classes.flavorTooltip}>
            <div>
              RAM:{' '}
              <b>
                {hotplugMemory
                  ? `${humanReadableSize(hotplugMemory * 1024 * 1024)} (hotplugged)`
                  : humanReadableSize(flavor?.ram * 1024 * 1024)}
              </b>
            </div>
            <div>
              vCPUs: <b>{hotplugVcpus ? `${hotplugVcpus} (hotplugged)` : flavor?.vcpus}</b>
            </div>
            <div>
              Disk: <b>{humanReadableSize(flavor?.disk * 1024 * 1024 * 1024)}</b>
            </div>
          </div>
        }
      >
        <Text variant="body2" className={classes.linkAppearance}>
          {value}
        </Text>
      </Tooltip>
    </div>
  )
}

const TenantCellComponent = ({ value, item }) => {
  useListAction(listTenants)
  const tenants = useSelector(tenantsSelector)
  const tenant = tenants.find((ten) => ten.id === value)
  return <Text variant="body2">{tenant?.name || tenant?.id || value}</Text>
}

const vmColumns = ({
  setErrorVm,
  userRole,
  hosts,
  allTenants,
}): GridViewColumn<SelectorModel>[] => {
  const columns = [
    {
      key: 'name',
      label: 'Name',
      width: 'medium',
      CellComponent: createGridLinkCell({
        routeToFn: ({ id }) =>
          routeManager.routes.openstack.vmDetails.path({ id, tab: IVmDetailsPageTabs.Overview }),
      }),
    },
    {
      key: 'links',
      label: 'Tasks',
      render: (val, item) => (
        <SimpleLink
          src={routeManager.routes.openstack.vmDetails.path({
            id: item?.id,
            tab: IVmDetailsPageTabs.Overview,
          })}
        >
          View Tasks
        </SimpleLink>
      ),
    },
    {
      key: 'id',
      label: 'UUID',
      display: false,
    },
    {
      key: 'state',
      label: 'State',
      CellComponent: (props) => (
        <VmStateCellComponent {...props} setErrorVm={setErrorVm} hosts={hosts} />
      ),
    },
    {
      key: 'clusterName',
      label: 'Cluster',
    },
    {
      key: 'tenantName',
      label: 'Tenant',
      CellComponent: TenantCellComponent,
    },
    {
      key: 'addresses',
      label: 'IP Addresses',
      CellComponent: IpAddressesCellComponent,
    },
    {
      key: 'flavorName',
      label: 'Flavor',
      CellComponent: FlavorCellComponent,
    },
    {
      key: 'imageName',
      label: 'Source Image',
    },
    {
      key: 'volumeNames',
      label: 'Volumes',
      render: (names, item) => {
        const volumeIds = item?.volumeIds
        const volumeDetails = item?.volumeDetails
        return (
          <div style={{ display: 'grid', gap: '8px' }}>
            {volumeIds?.map((volumeId) => (
              <div key={volumeId}>
                <SimpleLink
                  src={routeManager.routes.openstack.volumeDetails.path({
                    id: volumeId,
                    tab: IVolumeDetailsPageTabs.Overview,
                  })}
                >
                  {volumeDetails?.[volumeId]?.name || volumeId}
                </SimpleLink>
              </div>
            ))}
          </div>
        )
      },
      // Todo: cell component with links
    },
    {
      key: 'created',
      label: 'Created',
      formatFn: (value) => formatTableDate(value),
    },
    {
      key: 'securityGroupNames',
      label: 'Security Groups',
      render: (groups) => groups?.join(', '),
    },
    {
      key: 'serverGroup',
      label: 'Server Group',
      render: (group) => (group ? `${group?.name} (${group?.policy})` : ''),
      sortFn: (a, b) => (a?.name || '').localeCompare(b?.name || ''),
    },
    {
      key: 'os-extended-volumes:volumes_attached',
      label: 'Volume Delete on VM Termination',
      render: (val) => {
        return val?.[0]?.delete_on_termination === true ? 'Enabled' : 'Disabled'
      },
    },
    ...(allTenants
      ? []
      : [
          {
            key: 'expirationTime',
            label: 'Lease Expiration',
            render: (value, item) =>
              value ? `${leaseActionText[item?.leaseAction]} at ${formatTableDate(value)}` : null,
          },
        ]),
    {
      key: 'migrationPriority',
      label: 'Migration Priority',
      display: false,
      render: (value) =>
        options?.find((option) => option?.value === value)?.label || options[0]?.label,
    },
    {
      key: 'metadata',
      label: 'Metadata',
      CellComponent: createResourceLabelsCell({ type: 'table', separator: '=' }),
    },
  ]
  if (userRole === 'admin') {
    // @ts-ignore
    return insertAll(
      4,
      [
        {
          key: 'ownerName',
          label: 'Owner',
        },
        {
          key: 'OS-EXT-SRV-ATTR:hypervisor_hostname',
          label: 'Host',
          // Todo: cell component with link
          CellComponent: createGridLinkCell({
            routeToFn: (props) => {
              const hostId = props['OS-EXT-SRV-ATTR:host']
              if (!hostId) {
                return null
              }
              return routeManager.routes.openstack.hostDetails.path({
                id: hostId,
                tab: IHostDetailsPageTabs.Overview,
              })
            },
          }),
        },
      ],
      columns,
    )
  }
  // @ts-ignore
  return columns
}

/**
 * VirtualMachinesList - Displays VMs for a specific host
 * This is a common component used by VirtualMachinesListPage and HostDetailsPage
 * It includes all the common functionality for VMs list including Pagination, Sorting, Filtering, etc.
 * All the Actions - Batch Actions, Row Actions, are also included in this component
 */
export default function VirtualMachinesList({ host = null, pausePolling = false }) {
  const { allParams: params, updateGlobalParams, getParamsUpdater } = useGlobalParams(
    usePrefParams,
    defaultParams,
  )
  const classes = useStyles()
  const [selectedVms, setSelectedVms] = useState<SelectorModel[]>(null)
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false)
  const [showPowerDialog, setShowPowerDialog] = useState<boolean>(false)
  const [showRenameDialog, setShowRenameDialog] = useState<boolean>(false)
  const [showSnapshotDialog, setShowSnapshotDialog] = useState<boolean>(false)
  const [showRebuildDialog, setShowRebuildDialog] = useState<boolean>(false)
  const [showAddFixedIpModal, setShowAddFixedIpModal] = useState<boolean>(false)
  const [showRemoveFixedIpDialog, setShowRemoveFixedIpDialog] = useState<boolean>(false)
  const [showAddFloatingIpDialog, setShowAddFloatingIpDialog] = useState<boolean>(false)
  const [showAddServerGroupModal, setShowAddServerGroupModal] = useState<boolean>(false)
  const [showRemoveServerGroupModal, setShowRemoveServerGroupModal] = useState<boolean>(false)
  const [showRemoveFloatingIpDialog, setShowRemoveFloatingIpDialog] = useState<boolean>(false)
  const [showEditVmSecurityGroupsModal, setShowEditVmSecurityGroupsModal] = useState<boolean>(false)
  const [showEditVmMetadataModal, setShowEditVmMetadataModal] = useState<boolean>(false)
  const [showConfirmResizeDialog, setShowConfirmResizeDialog] = useState<boolean>(false)
  const [showResizeDialog, setShowResizeDialog] = useState<boolean>(false)
  const [showMigrateDialog, setShowMigrateDialog] = useState<boolean>(false)
  const [showManageVolumesModal, setShowManageVolumesModal] = useState<boolean>(false)
  const [showRescueDialog, setShowRescueDialog] = useState<boolean>(false)
  const [showUnrescueDialog, setShowUnrescueDialog] = useState<boolean>(false)
  const [showHotplugDialog, setShowHotplugDialog] = useState<boolean>(false)
  const [showCloneVmModal, setShowCloneVmModal] = useState<boolean>(false)
  const [showCustomLeaseModal, setShowCustomLeaseModal] = useState<boolean>(false)
  const [showMigrationPriorityDialog, setShowMigrationPriorityDialog] = useState<boolean>(false)
  const [showDeleteOnTerminationDialog, setShowDeleteOnTerminationDialog] = useState<boolean>(false)
  const [selectedPowerAction, setSelectedPowerAction] = useState<string>('')
  const [rebootType, setRebootType] = useState('SOFT')
  const [errorVm, setErrorVm] = useState<string>(null)

  const session = useSelector<RootState, SessionState>(prop(sessionStoreKey))
  const userRole = session?.userDetails?.role || ''
  const { dispatch } = store

  // Initialize from session (persisted across page navigations, shared with VirtualMachinesListPage)
  const [allTenants, setAllTenants] = useState(session?.vmsAllTenants ?? false)
  const [allOwners, setAllOwners] = useState(session?.vmsAllOwners ?? false)

  const isHostDetailsPage = host !== null

  const {
    // Data
    paginatedVMs,
    totalCount,

    // Loading states
    loading: activeLoading,
    polling: activePolling,
    refreshing: activeRefreshing,
    message: activeMessage,

    // Pagination
    pageMarkers,
    currentPage,
    pageSize,
    hasNextPage,
    isPaginationLoading,
    handlePageSizeChange,

    // Actions
    reload: activeReload,
    poll: activePoll,
    handlePageChange,
    handleRefresh,

    // Search
    searchParams,
    handleSearch,
    handleClearSearch,
    filterParamsForUI,

    // Sort
    sortKey,
    sortDir,
    handleSort,

    //statewise data
    vmStateCount,
    isStatewiseVMsLoading,
  } = useVirtualMachinesPagination({
    host: isHostDetailsPage ? host?.id : undefined,
    pageSize: session?.vmsPageSize || 10,
    allTenants: session?.vmsAllTenants || false,
    allOwners: session?.vmsAllOwners || false,
    sessionKey: isHostDetailsPage ? 'hostList' : 'vmList',
    sessionSearchParams: isHostDetailsPage ? 'hostVmsSearchParams' : 'vmsSearchParams',
    sessionSortKey: isHostDetailsPage ? 'hostVmsSortKey' : 'vmsSortKey',
    sessionSortDir: isHostDetailsPage ? 'hostVmsSortDir' : 'vmsSortDir',
    sessionPageSize: isHostDetailsPage ? 'hostVmsPageSize' : 'vmsPageSize',
    sessionVMCachedParams: isHostDetailsPage ? 'hostVmsCachedParams' : 'vmsCachedParams',
    sessionAllVMCachedParams: isHostDetailsPage ? 'hostAllVmsCachedParams' : 'vmsAllCachedParams',
    sessionSelfVMCachedParams: isHostDetailsPage
      ? 'hostSelfVmsCachedParams'
      : 'vmsSelfCachedParams',
    defaultSearchParams: defaultVMFilterParams,
  })

  const { prefs } = useScopedPreferences()
  const { currentTenant } = prefs

  // Cleanup effect: Clear host-specific session cache params when component unmounts
  useEffect(() => {
    const dependenciesNotLoaded = anyVmDependencyNotLoaded()
    if (dependenciesNotLoaded) {
      refreshVmDependencies()
    }
    return () => {
      // Only clear host-specific session cache params if we're on the host details page
      if (!isHostDetailsPage) {
        return
      }

      dispatch(
        sessionActions.updateSession({
          // hostVmsSearchParams: null,
          hostVmsSortKey: undefined,
          hostVmsSortDir: undefined,
          hostVmsCachedParams: undefined,
          hostAllVmsCachedParams: undefined,
          hostSelfVmsCachedParams: undefined,
        }),
      )
    }
  }, [])

  // Handle allTenants toggle with session persistence (shared with VirtualMachinesListPage)
  const handleAllTenantsToggle = useCallback(() => {
    const newValue = !allTenants
    setAllTenants(newValue)
    dispatch(sessionActions.updateSession({ vmsAllTenants: newValue }))
  }, [allTenants, dispatch])

  // Handle allOwners toggle with session persistence (shared with VirtualMachinesListPage)
  const handleAllOwnersToggle = useCallback(() => {
    const newValue = !allOwners
    setAllOwners(newValue)
    dispatch(sessionActions.updateSession({ vmsAllOwners: newValue }))
  }, [allOwners, dispatch])

  useEffect(() => {
    getNovaVersion()
  }, [])

  useListAction(listResmgrHosts)
  const hosts = useSelector(resmgrHostsSelector)

  useListAction(listMigrations)
  const { update: pollMigrations, updating: pollingMigrations } = useUpdateAction(
    getMigrationsSince,
  )

  const dropdownBatchActions: GridDropdownBatchActionGroup<SelectorModel>[] = useMemo(
    () => [
      {
        // icon: 'power-off',
        label: 'Power Actions',
        actions: [
          {
            label: 'Start',
            icon: 'circle-play',
            cond: (selected) => {
              return selected.some((vm) => ['shutoff', 'stopped'].includes(vm?.state))
            },
            disabledTooltip: (selected) =>
              selected.every((vm) => !['shutoff', 'stopped'].includes(vm?.state))
                ? ACTION_LIMITATION_TOOLTIPS.START
                : '',
            BatchActionButton: getGridDialogButton(
              PowerVirtualMachineDialog,
              { action: 'start' },
              { className: classes.dropdownAction },
            ),
          },
          {
            label: 'Stop',
            icon: 'stop',
            cond: (selected) => {
              return selected.some((vm) => vm?.state === 'active')
            },
            disabledTooltip: (selected) =>
              selected.every((vm) => vm?.state !== 'active') ? ACTION_LIMITATION_TOOLTIPS.STOP : '',
            BatchActionButton: getGridDialogButton(
              PowerVirtualMachineDialog,
              { action: 'stop' },
              { className: classes.dropdownAction },
            ),
          },
          {
            label: 'Reboot',
            icon: 'repeat',
            cond: (selected) => {
              return selected.some((vm) => vm?.state === 'active')
            },
            disabledTooltip: ACTION_LIMITATION_TOOLTIPS.REBOOT,
            BatchActionButton: getGridDialogButton(
              PowerVirtualMachineDialog,
              { action: 'reboot' },
              { className: classes.dropdownAction },
            ),
          },
          {
            label: 'Hard Reboot',
            icon: 'rotate-exclamation',
            cond: (selected) => {
              return selected.some((vm) => vm?.state === 'active')
            },
            disabledTooltip: ACTION_LIMITATION_TOOLTIPS.REBOOT,
            BatchActionButton: getGridDialogButton(
              PowerVirtualMachineDialog,
              { action: 'reboot', type: 'HARD' },
              { className: classes.dropdownAction },
            ),
          },
          {
            label: 'Suspend',
            icon: 'pause',
            cond: (selected) => {
              return selected.some((vm) => vm?.state === 'active')
            },
            disabledTooltip: (selected) =>
              selected.every((vm) => vm?.state !== 'active')
                ? ACTION_LIMITATION_TOOLTIPS.SUSPEND
                : '',
            BatchActionButton: getGridDialogButton(
              PowerVirtualMachineDialog,
              { action: 'suspend' },
              { className: classes.dropdownAction },
            ),
          },
          {
            label: 'Pause',
            icon: 'circle-pause',
            cond: (selected) => {
              return selected.some((vm) => vm?.state === 'active')
            },
            disabledTooltip: (selected) =>
              selected.every((vm) => vm?.state !== 'active')
                ? ACTION_LIMITATION_TOOLTIPS.PAUSE
                : '',
            BatchActionButton: getGridDialogButton(
              PowerVirtualMachineDialog,
              { action: 'pause' },
              { className: classes.dropdownAction },
            ),
          },
          {
            label: 'Unpause',
            icon: 'circle-play',
            cond: (selected) => {
              return selected.some((vm) => vm?.state === 'paused')
            },
            disabledTooltip: (selected) =>
              selected.every((vm) => vm?.state !== 'paused')
                ? ACTION_LIMITATION_TOOLTIPS.UNPAUSE
                : '',
            BatchActionButton: getGridDialogButton(
              PowerVirtualMachineDialog,
              { action: 'unpause' },
              { className: classes.dropdownAction },
            ),
          },
          {
            label: 'Resume',
            icon: 'play',
            cond: (selected) => {
              return selected.some((vm) => vm?.state === 'suspended')
            },
            disabledTooltip: (selected) =>
              selected.every((vm) => vm?.state !== 'suspended')
                ? ACTION_LIMITATION_TOOLTIPS.RESUME
                : '',
            BatchActionButton: getGridDialogButton(
              PowerVirtualMachineDialog,
              { action: 'resume' },
              { className: classes.dropdownAction },
            ),
          },
          {
            label: 'Rescue',
            icon: 'laptop-medical',
            cond: (selected) => selected.length === 1 && selected[0]?.state !== 'rescued',
            disabledTooltip: (selected) =>
              selected.length > 1
                ? ACTION_LIMITATION_TOOLTIPS.SINGLE_VM_ONLY
                : selected[0]?.state === 'rescued'
                ? ACTION_LIMITATION_TOOLTIPS.ALREADY_RESCUED
                : '',
            BatchActionButton: getGridDialogButton(RescueVmDialog, null, {
              className: classes.dropdownAction,
            }),
          },
          {
            label: 'Unrescue',
            icon: 'laptop-binary',
            cond: (selected) => selected.length === 1 && selected[0]?.state === 'rescued',
            disabledTooltip: (selected) =>
              selected.length > 1
                ? ACTION_LIMITATION_TOOLTIPS.SINGLE_VM_ONLY
                : selected[0]?.state !== 'rescued'
                ? ACTION_LIMITATION_TOOLTIPS.ALREADY_NOT_RESCUED
                : '',
            BatchActionButton: getGridDialogButton(UnrescueVmDialog, null, {
              className: classes.dropdownAction,
            }),
          },
        ],
      },
      {
        label: 'Networking',
        actions: [
          {
            label: 'Add Private IP',
            icon: 'square-plus',
            cond: (selected) => selected.length === 1,
            disabledTooltip: ACTION_LIMITATION_TOOLTIPS.ADD_PRIVATE_IP,
            BatchActionButton: getGridDialogButton(AddFixedIpModal, null, {
              className: classes.dropdownAction,
            }),
          },
          {
            label: 'Remove Private IP',
            icon: 'square-minus',
            cond: (selected) => selected.length === 1,
            disabledTooltip: ACTION_LIMITATION_TOOLTIPS.REMOVE_PRIVATE_IP,
            BatchActionButton: getGridDialogButton(RemoveFixedIpDialog, null, {
              className: classes.dropdownAction,
            }),
          },
          {
            label: 'Add Public IP',
            icon: 'square-plus',
            cond: (selected) => selected.length === 1,
            disabledTooltip: ACTION_LIMITATION_TOOLTIPS.ADD_PUBLIC_IP,
            BatchActionButton: getGridDialogButton(AddFloatingIpDialog, null, {
              className: classes.dropdownAction,
            }),
          },
          {
            label: 'Remove Public IP',
            icon: 'square-minus',
            cond: (selected) => selected.length === 1,
            disabledTooltip: ACTION_LIMITATION_TOOLTIPS.REMOVE_PUBLIC_IP,
            BatchActionButton: getGridDialogButton(RemoveFloatingIpDialog, null, {
              className: classes.dropdownAction,
            }),
          },
          {
            label: 'Edit Security Groups',
            icon: 'lock',
            cond: (selected) => selected.length === 1,
            disabledTooltip: ACTION_LIMITATION_TOOLTIPS.EDIT_SECURITY_GROUPS,
            BatchActionButton: getGridDialogButton(EditVmSecurityGroupsModal, null, {
              className: classes.dropdownAction,
            }),
          },
        ],
      },
      {
        // icon: 'square-ellipsis',
        label: 'Other',
        actions: [
          {
            label: 'Rename',
            icon: 'edit',
            cond: (selected) => selected.length === 1,
            disabledTooltip: ACTION_LIMITATION_TOOLTIPS.RENAME_VM,
            BatchActionButton: getGridDialogButton(RenameVirtualMachineDialog, null, {
              className: classes.dropdownAction,
            }),
          },
          {
            label: 'Change Owner',
            icon: 'user-edit',
            cond: (selected) => userRole === 'admin' && selected?.length === 1,
            disabledTooltip: (selected) =>
              selected?.length > 1
                ? ACTION_LIMITATION_TOOLTIPS.CHANGE_OWNER_VM
                : userRole !== 'admin'
                ? ACTION_LIMITATION_TOOLTIPS.ADMIN_ONLY
                : '',
            BatchActionButton: getGridDialogButton(ChangeOwnerDialog, null, {
              className: classes.dropdownAction,
            }),
          },
          {
            label: 'Snapshot',
            icon: 'camera',
            cond: (selected) =>
              selected.length === 1 &&
              !selected?.some((vm) => vm?.flavor?.extra_specs?.['pci_passthrough:alias']),
            disabledTooltip: (selected) =>
              selected.length > 1
                ? ACTION_LIMITATION_TOOLTIPS.SNAPSHOT_VM
                : selected?.every((vm) => vm?.flavor?.extra_specs?.['pci_passthrough:alias'])
                ? TOOLTIP_MESSAGES.GPU_PASSTHROUGH_SNAPSHOT
                : '',
            BatchActionButton: getGridDialogButton(SnapshotVirtualMachineDialog, null, {
              className: classes.dropdownAction,
            }),
          },
          {
            label: 'Clone',
            icon: 'clone',
            cond: (selected) => selected.length === 1,
            disabledTooltip: ACTION_LIMITATION_TOOLTIPS.CLONE_VM,
            BatchActionButton: getGridDialogButton(CloneVirtualMachineModal, null, {
              className: classes.dropdownAction,
            }),
          },
          {
            label: 'Manage Volumes',
            icon: 'hard-drive',
            cond: (vms) =>
              vms.length === 1 &&
              ['active', 'suspended', 'stopped'].includes(vms[0].state) &&
              isVolumeBackedVm(vms[0]),
            disabledTooltip: (selected) =>
              selected.length > 1
                ? ACTION_LIMITATION_TOOLTIPS.SINGLE_VM_ONLY
                : !['active', 'suspended', 'stopped'].includes(selected[0]?.state)
                ? ACTION_LIMITATION_TOOLTIPS.MANAGE_VOLUMES_STATE
                : !isVolumeBackedVm(selected[0])
                ? VOLUME_BACKED_VM_ERROR_MSG
                : '',
            BatchActionButton: getGridDialogButton(ManageVolumesModal, null, {
              className: classes.dropdownAction,
            }),
          },
          {
            label: 'Edit Metadata',
            icon: 'tags',
            cond: (selected) => selected.length === 1,
            disabledTooltip: ACTION_LIMITATION_TOOLTIPS.EDIT_METADATA,
            BatchActionButton: getGridDialogButton(EditVmMetadataModal, null, {
              className: classes.dropdownAction,
            }),
          },
          {
            label: 'Resize',
            icon: 'up-right-and-down-left-from-center',
            cond: (selected) => selected.length === 1 && selected[0]?.state !== 'resized',
            disabledTooltip: (selected) =>
              selected.length > 1
                ? ACTION_LIMITATION_TOOLTIPS.RESIZE_VM
                : selected[0]?.state === 'resized'
                ? ACTION_LIMITATION_TOOLTIPS.ALREADY_RESIZED
                : '',
            BatchActionButton: getGridDialogButton(ResizeVirtualMachineDialog, null, {
              className: classes.dropdownAction,
            }),
          },
          {
            label: 'Confirm Resize',
            icon: 'box-check',
            cond: (selected) => selected.length === 1 && selected[0]?.state === 'resized',
            disabledTooltip: (selected) =>
              selected.length > 1
                ? ACTION_LIMITATION_TOOLTIPS.CONFIRM_RESIZE_VM
                : selected[0]?.state !== 'resized'
                ? ACTION_LIMITATION_TOOLTIPS.NOT_RESIZED
                : '',
            BatchActionButton: getGridDialogButton(ConfirmResizeDialog, null, {
              className: classes.dropdownAction,
            }),
          },
          {
            label: 'Hotplug',
            icon: 'plug-circle-plus',
            cond: (selected) =>
              selected.length === 1 && !!selected[0]?.metadata?.['HOTPLUG_MEMORY'],
            disabledTooltip: (selected) =>
              selected.length > 1
                ? ACTION_LIMITATION_TOOLTIPS.HOTPLUG_ONE_VM
                : !selected[0]?.metadata?.['HOTPLUG_MEMORY']
                ? ACTION_LIMITATION_TOOLTIPS.VM_NOT_HOTPLUGGED
                : '',
            BatchActionButton: getGridDialogButton(HotplugVmDialog, null, {
              className: classes.dropdownAction,
            }),
          },
          {
            label: 'Migrate',
            icon: 'database',
            cond: (selected) => canMigrateVMs(userRole, selected),
            hideCond: (selected) => userRole !== 'admin',
            disabledTooltip: (selected) => <GetDisabledTooltipForVMs selected={selected} />,
            BatchActionButton: getGridDialogButton(MigrateVirtualMachineDialog, null, {
              className: classes.dropdownAction,
            }),
          },
          {
            label: 'Migration Priority',
            icon: 'ranking-star',
            cond: (selected) => selected.length === 1 && userRole === 'admin',
            hideCond: (selected) => userRole !== 'admin',
            disabledTooltip: (selected) =>
              selected.length > 1
                ? ACTION_LIMITATION_TOOLTIPS.UPDATE_MIGRATION_PRIORITY
                : userRole !== 'admin'
                ? ACTION_LIMITATION_TOOLTIPS.MIGRATION_PRIORITY_ADMIN_ONLY
                : '',
            BatchActionButton: getGridDialogButton(UpdateMigrationPriorityDialog, null, {
              className: classes.dropdownAction,
            }),
          },
          {
            label: 'Rebuild',
            icon: 'screwdriver-wrench',
            cond: (selected) => selected.length === 1,
            disabledTooltip: ACTION_LIMITATION_TOOLTIPS.REBUILD_VM,
            BatchActionButton: getGridDialogButton(RebuildVirtualMachineDialog, null, {
              className: classes.dropdownAction,
            }),
          },
          {
            label: 'Customize Lease',
            icon: 'key',
            cond: (selected) =>
              selected.length === 1 &&
              selected[0]?.tenant_id === currentTenant &&
              !!selected[0]?.leasePolicy,
            disabledTooltip: (selected) =>
              selected.length > 1
                ? ACTION_LIMITATION_TOOLTIPS.CUSTOMIZE_LEASE
                : selected[0]?.tenant_id !== currentTenant
                ? ACTION_LIMITATION_TOOLTIPS.VM_NOT_OWNED
                : !selected[0]?.leasePolicy
                ? ACTION_LIMITATION_TOOLTIPS.VM_NOT_LEASED
                : '',
            keepRowsSelected: false,
            // handleAction: (vms) => {
            //   setSelectedVms(vms)
            //   setShowCustomLeaseModal(true)
            //   return true // returned true to remove selection after action. this works even without returning true but just to be explicit
            // },
            BatchActionButton: getGridDialogButton(CustomVmLeaseModal, null, {
              className: classes.dropdownAction,
            }),
          },
          {
            label: 'Enable Root Volume Delete on Termination',
            icon: 'trash',
            cond: (selected) =>
              selected?.length === 1 &&
              ['active', 'paused', 'resized'].includes(selected[0]?.state),
            hideCond: (selected) => getHideCondForDeleteOnTermination(selected, true),
            BatchActionButton: getGridDialogButton(DeleteOnTerminationDialog, null, {
              className: classes.dropdownAction,
            }),
          },
          {
            label: 'Disable Root Volume Delete on Termination',
            icon: 'trash-slash',
            cond: (selected) =>
              selected?.length === 1 &&
              ['active', 'paused', 'resized'].includes(selected[0]?.state),
            hideCond: (selected) => getHideCondForDeleteOnTermination(selected, false),
            BatchActionButton: getGridDialogButton(DeleteOnTerminationDialog, null, {
              className: classes.dropdownAction,
            }),
          },
          {
            label: 'Add Server Group',
            icon: 'square-plus',
            cond: (selected) =>
              selected.length === 1 &&
              selected?.every(
                (vm) => !Boolean(vm?.serverGroup) && ['active', 'stopped'].includes(vm?.state),
              ),
            disabledTooltip: (selected) =>
              selected.length > 1
                ? ACTION_LIMITATION_TOOLTIPS.ADD_SERVER_GROUP_ONE_VM
                : selected?.some(
                    (vm) => Boolean(vm?.serverGroup) || !['active', 'stopped'].includes(vm?.state),
                  )
                ? ACTION_LIMITATION_TOOLTIPS.VM_ALREADY_IN_SERVER_GROUP
                : '',
            BatchActionButton: getGridDialogButton(AddServerGroupDialog, null, {
              className: classes.dropdownAction,
            }),
          },
          {
            label: 'Remove Server Group',
            icon: 'square-minus',
            cond: (selected) =>
              selected?.every(
                (vm) => Boolean(vm?.serverGroup) && ['active', 'stopped'].includes(vm?.state),
              ),
            disabledTooltip: (selected) =>
              selected?.some(
                (vm) => !Boolean(vm?.serverGroup) || !['active', 'stopped'].includes(vm?.state),
              )
                ? 'The VM is not in any server group to remove. Please select a VM that is in a server group to remove.'
                : '',
            BatchActionButton: getGridDialogButton(RemoveServerGroupDialog, null, {
              className: classes.dropdownAction,
            }),
          },
        ],
      },
    ],
    [classes, userRole, currentTenant],
  )

  const batchActions: GridBatchActionSpec<SelectorModel>[] = useMemo(
    () => [
      {
        cond: (vms) => {
          return vms?.length === 1 && ['shutoff', 'stopped'].includes(vms?.[0]?.state)
        },
        disabledTooltip: (vms) =>
          vms?.length > 1
            ? ACTION_LIMITATION_TOOLTIPS.START_ONE_VM
            : !['shutoff', 'stopped'].includes(vms?.[0]?.state)
            ? ACTION_LIMITATION_TOOLTIPS.CANNOT_START_RUNNING
            : '',
        label: 'Start',
        icon: 'circle-play',
        iconOnly: true,
        tooltip: TOOLTIP_MESSAGES.START,
        handleAction: (vms) => {
          setSelectedVms(vms)
          setSelectedPowerAction('start')
          setShowPowerDialog(true)
        },
        refreshAfterSuccess: true,
      },
      {
        cond: (vms) => {
          return vms?.length === 1 && vms?.[0]?.state === 'active'
        },
        disabledTooltip: (vms) =>
          vms?.length > 1
            ? ACTION_LIMITATION_TOOLTIPS.STOP_ONE_VM
            : vms?.[0]?.state !== 'active'
            ? ACTION_LIMITATION_TOOLTIPS.CANNOT_STOP_STOPPED
            : '',
        label: 'Stop',
        icon: 'stop',
        iconOnly: true,
        tooltip: TOOLTIP_MESSAGES.STOP,
        handleAction: (vms) => {
          setSelectedVms(vms)
          setSelectedPowerAction('stop')
          setShowPowerDialog(true)
        },
        refreshAfterSuccess: true,
      },
      {
        cond: (vms) => {
          return vms?.length === 1
        },
        disabledTooltip: ACTION_LIMITATION_TOOLTIPS.CONSOLE_VM,
        label: 'Console',
        icon: 'terminal',
        iconOnly: true,
        tooltip: TOOLTIP_MESSAGES.CONSOLE,
        handleAction: ([vm]) => {
          const openConsole = async () => {
            const url = await getConsoleEndpoint({ id: vm.id })
            const withVmName = `${url}&instance=${vm?.name || vm?.id}`
            window.open(withVmName)
          }
          openConsole()
        },
        refreshAfterSuccess: true,
      },
      // {
      //   cond: (vm) => {
      //     return vm?.length >= 1
      //   },
      //   label: 'Delete VM',
      //   icon: 'trash-alt',
      //   iconOnly: true,
      //   handleAction: (vms) => {
      //     console.log(vms, 'vms')
      //     setSelectedVms(vms)
      //     setShowDeleteDialog(true)
      //   },
      //   refreshAfterSuccess: true,
      // },
    ],
    [setSelectedVms, setSelectedPowerAction, setShowPowerDialog],
  )

  // Adding this in later when I figure out bug
  const rowMenuItems: Array<GridRowMenuItemSpec<SelectorModel>> = useMemo(
    () => [
      {
        label: 'Power Actions',
        items: [
          {
            label: 'Start',
            icon: 'circle-play',
            handleClick: (vm) => {
              setSelectedVms([vm])
              setSelectedPowerAction('start')
              setShowPowerDialog(true)
            },
            cond: (selected) => {
              return ['shutoff', 'stopped'].includes(selected?.state)
            },
            disabledTooltip: 'Cannot start a running VM',
            refreshAfterSuccess: true,
            hideIfDisabled: false,
          },
          {
            label: 'Stop',
            icon: 'stop',
            handleClick: (vm) => {
              setSelectedVms([vm])
              setSelectedPowerAction('stop')
              setShowPowerDialog(true)
            },
            cond: (selected) => {
              return selected?.state === 'active'
            },
            disabledTooltip: 'Cannot stop a stopped VM',
            refreshAfterSuccess: true,
            hideIfDisabled: false,
          },
          {
            label: 'Reboot',
            icon: 'repeat',
            handleClick: (vm) => {
              setSelectedVms([vm])
              setSelectedPowerAction('reboot')
              setRebootType('SOFT')
              setShowPowerDialog(true)
            },
            cond: (selected) => {
              return selected?.state === 'active'
            },
            disabledTooltip: 'You can only reboot VMs that are currently running',
            refreshAfterSuccess: true,
            hideIfDisabled: false,
          },
          {
            label: 'Hard Reboot',
            icon: 'rotate-exclamation',
            handleClick: (vm) => {
              setSelectedVms([vm])
              setSelectedPowerAction('reboot')
              setRebootType('HARD')
              setShowPowerDialog(true)
            },
            cond: (selected) => {
              return selected?.state === 'active'
            },
            disabledTooltip: 'You can only reboot VMs that are currently running',
            refreshAfterSuccess: true,
            hideIfDisabled: false,
          },
          {
            label: 'Suspend',
            icon: 'pause',
            handleClick: (vm) => {
              setSelectedVms([vm])
              setSelectedPowerAction('suspend')
              setShowPowerDialog(true)
            },
            cond: (selected) => {
              return selected?.state === 'active'
            },
            disabledTooltip: 'Cannot suspend a non-running VM',
            refreshAfterSuccess: true,
            hideIfDisabled: false,
          },
          {
            label: 'Pause',
            icon: 'circle-pause',
            cond: (selected) => {
              return selected?.state === 'active'
            },
            disabledTooltip: ACTION_LIMITATION_TOOLTIPS.PAUSE,
            handleClick: (vm) => {
              setSelectedVms([vm])
              setSelectedPowerAction('pause')
              setShowPowerDialog(true)
            },
          },
          {
            label: 'Unpause',
            icon: 'circle-play',
            cond: (selected) => {
              return selected?.state === 'paused'
            },
            disabledTooltip: ACTION_LIMITATION_TOOLTIPS.UNPAUSE,
            handleClick: (vm) => {
              setSelectedVms([vm])
              setSelectedPowerAction('unpause')
              setShowPowerDialog(true)
            },
          },
          {
            label: 'Resume',
            icon: 'play',
            handleClick: (vm) => {
              setSelectedVms([vm])
              setSelectedPowerAction('resume')
              setShowPowerDialog(true)
            },
            cond: (selected) => {
              return selected?.state === 'suspended'
            },
            disabledTooltip: ACTION_LIMITATION_TOOLTIPS.RESUME,
            refreshAfterSuccess: true,
            hideIfDisabled: false,
          },
          {
            label: 'Rescue',
            icon: 'laptop-medical',
            cond: (selected) => {
              return selected?.state !== 'rescued'
            },
            disabledTooltip: ACTION_LIMITATION_TOOLTIPS.RESCUE,
            handleClick: (vm) => {
              setSelectedVms([vm])
              setShowRescueDialog(true)
            },
            refreshAfterSuccess: true,
            hideIfDisabled: false,
          },
          {
            label: 'Unrescue',
            icon: 'laptop-binary',
            cond: (selected) => {
              return selected?.state === 'rescued'
            },
            disabledTooltip: ACTION_LIMITATION_TOOLTIPS.UNRESCUE,
            handleClick: (vm) => {
              setSelectedVms([vm])
              setShowUnrescueDialog(true)
            },
            refreshAfterSuccess: true,
            hideIfDisabled: false,
          },
        ],
      },
      {
        label: 'Network Actions',
        items: [
          {
            label: 'Add Private IP',
            icon: 'square-plus',
            handleClick: (vm) => {
              setSelectedVms([vm])
              setShowAddFixedIpModal(true)
            },
            refreshAfterSuccess: true,
            hideIfDisabled: false,
          },
          {
            label: 'Remove Private IP',
            icon: 'square-minus',
            handleClick: (vm) => {
              setSelectedVms([vm])
              setShowRemoveFixedIpDialog(true)
            },
            refreshAfterSuccess: true,
            hideIfDisabled: false,
          },
          {
            label: 'Add Public IP',
            icon: 'square-plus',
            handleClick: (vm) => {
              setSelectedVms([vm])
              setShowAddFloatingIpDialog(true)
            },
            refreshAfterSuccess: true,
            hideIfDisabled: false,
          },
          {
            label: 'Remove Public IP',
            icon: 'square-minus',
            handleClick: (vm) => {
              setSelectedVms([vm])
              setShowRemoveFloatingIpDialog(true)
            },
            refreshAfterSuccess: true,
            hideIfDisabled: false,
          },
          {
            label: 'Edit Security Groups',
            icon: 'lock',
            handleClick: (vm) => {
              setSelectedVms([vm])
              setShowEditVmSecurityGroupsModal(true)
            },
            refreshAfterSuccess: true,
            hideIfDisabled: false,
          },
        ],
      },
      {
        label: 'Other Actions',
        items: [
          {
            label: 'Delete',
            icon: 'trash-alt',
            handleClick: (vm) => {
              setSelectedVms([vm])
              setShowDeleteDialog(true)
            },
            refreshAfterSuccess: true,
            hideIfDisabled: false,
          },
          {
            label: 'Rename',
            icon: 'edit',
            handleClick: (vm) => {
              setSelectedVms([vm])
              setShowRenameDialog(true)
            },
            refreshAfterSuccess: true,
            hideIfDisabled: false,
          },
          {
            label: 'Snapshot',
            icon: 'camera',
            cond: (selected) => !selected?.flavor?.extra_specs?.['pci_passthrough:alias'],
            disabledTooltip: 'Snapshot is not supported for GPU passthrough VMs',
            handleClick: (vm) => {
              setSelectedVms([vm])
              setShowSnapshotDialog(true)
            },
            refreshAfterSuccess: true,
            hideIfDisabled: false,
          },
          {
            label: 'Clone',
            icon: 'clone',
            handleClick: (vm) => {
              setSelectedVms([vm])
              setShowCloneVmModal(true)
            },
            refreshAfterSuccess: true,
            hideIfDisabled: false,
          },
          {
            label: 'Manage Volumes',
            icon: 'hard-drive',
            handleClick: (vm) => {
              setSelectedVms([vm])
              setShowManageVolumesModal(true)
            },
            cond: (selected) =>
              ['active', 'suspended', 'stopped'].includes(selected?.state) &&
              isVolumeBackedVm(selected),
            disabledTooltip: (selected) =>
              !['active', 'suspended', 'stopped'].includes(selected?.state)
                ? ACTION_LIMITATION_TOOLTIPS.MANAGE_VOLUMES
                : !isVolumeBackedVm(selected)
                ? VOLUME_BACKED_VM_ERROR_MSG
                : '',
            refreshAfterSuccess: true,
            hideIfDisabled: false,
          },
          {
            label: 'Edit Metadata',
            icon: 'tags',
            handleClick: (vm) => {
              setSelectedVms([vm])
              setShowEditVmMetadataModal(true)
            },
            refreshAfterSuccess: true,
            hideIfDisabled: false,
          },
          {
            label: 'Resize',
            icon: 'up-right-and-down-left-from-center',
            handleClick: (vm) => {
              setSelectedVms([vm])
              setShowResizeDialog(true)
            },
            cond: (selected) => selected?.state !== 'resized',
            disabledTooltip: (selected) =>
              selected?.state === 'resized' ? 'VM is already resized' : '',
            refreshAfterSuccess: true,
            hideIfDisabled: false,
          },
          {
            label: 'Confirm Resize',
            icon: 'box-check',
            handleClick: (vm) => {
              setSelectedVms([vm])
              setShowConfirmResizeDialog(true)
            },
            cond: (selected) => selected?.state === 'resized',
            disabledTooltip: 'VM is not resized. Please select a resized VM to confirm resize.',
            refreshAfterSuccess: true,
            hideIfDisabled: false,
          },
          {
            label: 'Hotplug',
            icon: 'plug-circle-plus',
            handleClick: (vm) => {
              setSelectedVms([vm])
              setShowHotplugDialog(true)
            },
            cond: (selected) => {
              return !!selected?.metadata?.['HOTPLUG_MEMORY']
            },
            disabledTooltip: 'VM is not hotplugged. Please select a hotplugged VM to hotplug.',
            refreshAfterSuccess: true,
            hideIfDisabled: false,
          },
          {
            label: 'Migrate',
            icon: 'database',
            handleClick: (vm) => {
              setSelectedVms([vm])
              setShowMigrateDialog(true)
            },
            cond: (selected) => canMigrateVMs(userRole, [selected]),
            hideCond: () => userRole !== 'admin',
            disabledTooltip: (selected) => <GetDisabledTooltipForVMs selected={[selected]} />,
            refreshAfterSuccess: true,
            hideIfDisabled: false,
          },
          {
            label: 'Migration Priority',
            icon: 'ranking-star',
            handleClick: (vm) => {
              setSelectedVms([vm])
              setShowMigrationPriorityDialog(true)
            },
            cond: (selected) => userRole === 'admin',
            disabledTooltip: 'Only admins can update migration priority',
            hideCond: () => userRole !== 'admin',
            refreshAfterSuccess: true,
            hideIfDisabled: false,
          },
          {
            label: 'Rebuild',
            icon: 'screwdriver-wrench',
            handleClick: (vm) => {
              setSelectedVms([vm])
              setShowRebuildDialog(true)
            },
            refreshAfterSuccess: true,
            hideIfDisabled: false,
          },
          {
            label: 'Customize Lease',
            icon: 'key',
            handleClick: (vm) => {
              setSelectedVms([vm])
              setShowCustomLeaseModal(true)
            },
            cond: (selected) => selected.tenant_id === currentTenant && !!selected.leasePolicy,
            disabledTooltip: (selected) =>
              selected[0]?.tenant_id !== currentTenant
                ? 'VM is not owned by you. Please select a VM owned by you to customize lease.'
                : !selected[0]?.leasePolicy
                ? 'VM is not leased. Please select a leased VM to customize lease.'
                : '',
            refreshAfterSuccess: true,
            hideIfDisabled: false,
          },
          {
            label: 'Enable Root Volume Delete on Termination',
            icon: 'trash',
            handleClick: (vm) => {
              setSelectedVms([vm])
              setShowDeleteOnTerminationDialog(true)
            },
            cond: (selected) =>
              !(
                ['active', 'paused', 'resized'].includes(selected?.state) &&
                getHideCondForDeleteOnTermination([selected], true)
              ),
            disabledTooltip:
              'You can only enable delete on termination for an active, paused or resized VM',
            refreshAfterSuccess: true,
            hideIfDisabled: true,
          },
          {
            label: 'Disable Root Volume Delete on Termination',
            icon: 'trash-slash',
            handleClick: (vm) => {
              setSelectedVms([vm])
              setShowDeleteOnTerminationDialog(true)
            },
            cond: (selected) =>
              !(
                ['active', 'paused', 'resized'].includes(selected?.state) &&
                getHideCondForDeleteOnTermination([selected], false)
              ),
            disabledTooltip:
              'You can only disable delete on termination for an active, paused or resized VM',
            refreshAfterSuccess: true,
            hideIfDisabled: true,
          },
          {
            label: 'Add Server Group',
            icon: 'square-plus',
            cond: (selected) =>
              !Boolean(selected?.serverGroup) && ['active', 'stopped'].includes(selected?.state),
            disabledTooltip:
              'The VM is already in a server group. Please select a VM that is not in a server group to add.',
            handleClick: (vm) => {
              setSelectedVms([vm])
              setShowAddServerGroupModal(true)
            },
          },
          {
            label: 'Remove Server Group',
            icon: 'square-minus',
            cond: (selected) =>
              Boolean(selected?.serverGroup) && ['active', 'stopped'].includes(selected?.state),
            disabledTooltip:
              'The VM is not in any server group to remove. Please select a VM that is in a server group to remove.',
            handleClick: (vm) => {
              setSelectedVms([vm])
              setShowRemoveServerGroupModal(true)
            },
          },
        ],
      },
    ],
    [userRole],
  )

  const columns = useMemo(() => vmColumns({ setErrorVm, userRole, hosts, allTenants }), [
    setErrorVm,
    userRole,
    hosts,
    allTenants,
  ])

  return (
    <>
      <PollingData
        hidden
        loading={activePolling}
        // @ts-ignore
        onReload={activePoll}
        refreshDuration={1000 * 30}
        pausePolling={pausePolling}
      />
      <PollingData
        hidden
        loading={pollingMigrations}
        // @ts-ignore
        onReload={pollMigrations}
        refreshDuration={1000 * 30}
        pausePolling={pausePolling}
      />
      {/* {isHostDetailsPage && (
        <QuotaUsageData vms={vmStateCount} loadingVMs={isStatewiseVMsLoading} />
      )} */}
      {!isHostDetailsPage && (
        <CreateVirtualMachineModal
          addRoute={routeManager.routes.openstack.deployVm}
          reloadVirtualMachines={handleRefresh}
        />
      )}

      {showDeleteDialog && (
        <DeleteVirtualMachineDialog onClose={() => setShowDeleteDialog(false)} rows={selectedVms} />
      )}
      {showPowerDialog && (
        <PowerVirtualMachineDialog
          onClose={() => setShowPowerDialog(false)}
          rows={selectedVms}
          action={selectedPowerAction}
          type={rebootType}
        />
      )}
      {showRenameDialog && (
        <RenameVirtualMachineDialog onClose={() => setShowRenameDialog(false)} rows={selectedVms} />
      )}
      {showSnapshotDialog && (
        <SnapshotVirtualMachineDialog
          onClose={() => setShowSnapshotDialog(false)}
          rows={selectedVms}
        />
      )}
      {showRebuildDialog && (
        <RebuildVirtualMachineDialog
          onClose={() => setShowRebuildDialog(false)}
          rows={selectedVms}
        />
      )}
      {showAddFixedIpModal && (
        <AddFixedIpModal onClose={() => setShowAddFixedIpModal(false)} rows={selectedVms} />
      )}
      {showRemoveFixedIpDialog && (
        <RemoveFixedIpDialog onClose={() => setShowRemoveFixedIpDialog(false)} rows={selectedVms} />
      )}
      {showAddFloatingIpDialog && (
        <AddFloatingIpDialog onClose={() => setShowAddFloatingIpDialog(false)} rows={selectedVms} />
      )}
      {showRemoveFloatingIpDialog && (
        <RemoveFloatingIpDialog
          onClose={() => setShowRemoveFloatingIpDialog(false)}
          rows={selectedVms}
        />
      )}
      {showEditVmSecurityGroupsModal && (
        <EditVmSecurityGroupsModal
          onClose={() => setShowEditVmSecurityGroupsModal(false)}
          rows={selectedVms}
        />
      )}
      {showEditVmMetadataModal && (
        <EditVmMetadataModal onClose={() => setShowEditVmMetadataModal(false)} rows={selectedVms} />
      )}
      {showResizeDialog && (
        <ResizeVirtualMachineDialog onClose={() => setShowResizeDialog(false)} rows={selectedVms} />
      )}
      {showConfirmResizeDialog && (
        <ConfirmResizeDialog onClose={() => setShowConfirmResizeDialog(false)} rows={selectedVms} />
      )}
      {showMigrateDialog && (
        <MigrateVirtualMachineDialog
          onClose={() => setShowMigrateDialog(false)}
          rows={selectedVms}
        />
      )}
      {showMigrationPriorityDialog && (
        <UpdateMigrationPriorityDialog
          onClose={() => setShowMigrationPriorityDialog(false)}
          rows={selectedVms}
        />
      )}
      {showManageVolumesModal && (
        <ManageVolumesModal onClose={() => setShowManageVolumesModal(false)} rows={selectedVms} />
      )}
      {showRescueDialog && (
        <RescueVmDialog onClose={() => setShowRescueDialog(false)} rows={selectedVms} />
      )}
      {showUnrescueDialog && (
        <UnrescueVmDialog onClose={() => setShowUnrescueDialog(false)} rows={selectedVms} />
      )}
      {showHotplugDialog && (
        <HotplugVmDialog onClose={() => setShowHotplugDialog(false)} rows={selectedVms} />
      )}
      {showCloneVmModal && (
        <CloneVirtualMachineModal onClose={() => setShowCloneVmModal(false)} rows={selectedVms} />
      )}
      {showCustomLeaseModal && (
        <CustomVmLeaseModal onClose={() => setShowCustomLeaseModal(false)} rows={selectedVms} />
      )}
      {errorVm && (
        <VmErrorModal vmId={errorVm} hostName={errorVm} onClose={() => setErrorVm(null)} />
      )}
      {/* Server-side search and sort */}
      <VMServerSideSearch
        onSearch={handleSearch}
        onClear={handleClearSearch}
        isSearching={isPaginationLoading}
        isLoading={activeLoading}
        initialSearchParams={searchParams}
        initialFilterParams={filterParamsForUI}
        allTenants={allTenants}
        excludeFilters={['host']}
        togglesComponent={
          <>
            {userRole === 'admin' ? (
              <ToggleSwitch
                active={allTenants}
                onClick={handleAllTenantsToggle}
                label="View VMs in All Tenants"
              />
            ) : userRole === 'member' ? (
              <ToggleSwitch
                active={allOwners}
                onClick={handleAllOwnersToggle}
                label="View VMs for All Owners"
              />
            ) : null}
          </>
        }
        sortComponent={
          <VMServerSideSort
            onSort={handleSort}
            isLoading={activeLoading}
            defaultSortKey={sortKey}
            defaultSortDir={sortDir}
          />
        }
      />
      <ListContainer<ModelDataKey, SelectorModel>
        dataKey={DataKeys.OpenstackVirtualMachines}
        emptyContent={
          userRole === 'member' && !allOwners ? 'You have no VMs on this tenant' : undefined
        }
        showBreadcrumbs={false}
        // searchTargets={searchTargets}
        // searchFunctions={searchFunctions}
        searchBoxLabel="Page Search"
        disableSearch={true}
        disableSorting={true}
        uniqueIdentifier="id"
        loading={activeLoading}
        loadingMessage={activeMessage}
        onRefresh={() => {
          refreshVmDependencies()
          handleRefresh()
        }}
        // Pass only current page data - search will work on these items only
        data={paginatedVMs}
        columns={columns}
        getParamsUpdater={getParamsUpdater}
        // @ts-ignore
        deleteAction={deleteVirtualMachine}
        deleteCond={(vms) => {
          return vms?.length > 0
        }}
        // This requires DeleteDialogComponent to also trigger list deselection
        DeleteDialogComponent={DeleteVirtualMachineDialog}
        batchActions={batchActions}
        dropdownBatchActions={dropdownBatchActions}
        // Some bug when rowMenuItems is active?
        rowMenuItems={rowMenuItems}
        rowMenuOffset={{ vertical: 30 }}
        // refreshing={activeRefreshing}
        showRowMenuForSingleRowActions
        multiSelection
        iconOnly
        rowIsSelectableFn={(vm) => !(vm.state === 'deleted' || vm.taskState === 'deleting')}
        // Disable client-side pagination since we're using server-side
        disablePagination={true}
        // Custom server-side pagination
        CustomPaginationComponent={() => (
          <VMServerSidePagination
            dataKey={
              allTenants
                ? DataKeys.AllOpenstackVirtualMachines
                : userRole === 'member' && !allOwners
                ? DataKeys.SelfOpenstackVirtualMachines
                : DataKeys.OpenstackVirtualMachines
            }
            currentPage={currentPage}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            totalCount={totalCount}
            hasNextPage={hasNextPage}
            nextMarker={pageMarkers[currentPage + 1]}
          />
        )}
        {...pick(listTablePrefs, params)}
      />
    </>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  addresses: {
    display: 'grid',
    gap: 8,
  },

  dropdownAction: {
    background: theme.components.dropdown.background,
    border: 'none',
    '& .button-text': {
      color: theme.components.dropdown.color,
      justifyContent: 'start',
    },
  },
  nowrap: {
    whiteSpace: 'nowrap',
  },
  inlineBlock: {
    display: 'inline-block',
  },
  flavorTooltip: {
    padding: 8,
  },
  linkAppearance: {
    color: theme.palette.primary.main,
  },
}))
